import React from 'react';
import { push } from 'connected-react-router';
import compose from 'recompose/compose';
import { connect } from 'react-redux';
import _ from 'lodash';
import { projectPickerSelectors } from '../../redux/modules/projectPicker';
import { projectCohortPickerSelectors } from '../../redux/modules/projectCohortPicker';

const NULL_PROJECT_CHILD_KEY = 'no-active-project';
const NULL_COHORT_CHILD_KEY = 'no-active-cohort';

export type WatchForProjectChangeOptions = {
  projectChangeRedirectUrl?: string;
  remountOnChange?: boolean;
  remountOnCohortChange?: boolean;
  onlyMountWithProject?: boolean;
  includeActiveProject?: boolean;
  buildProjectChangeRedirectUrl?: (projectId: string) => void;
};

const watchForProjectChange =
  (options: WatchForProjectChangeOptions = {}) =>
  (WrappedComponent: any) => {
    const {
      projectChangeRedirectUrl,
      remountOnChange,
      remountOnCohortChange,
      onlyMountWithProject,
      includeActiveProject = false,
      buildProjectChangeRedirectUrl,
    } = options;

    class WatchForProjectChange extends React.PureComponent {
      componentDidUpdate(prevProps: any) {
        if (!projectChangeRedirectUrl && !buildProjectChangeRedirectUrl) return;

        const prevProjectId = _.get(
          prevProps,
          'remountOnProjectChangeActiveProject.id',
        );
        const curProjectId = _.get(
          this.props,
          'remountOnProjectChangeActiveProject.id',
        );

        const projectIdUnselected = !!prevProjectId && !curProjectId;
        const projectIdSwitched =
          prevProjectId && curProjectId && prevProjectId !== curProjectId;

        if (projectIdUnselected || projectIdSwitched) {
          if (projectChangeRedirectUrl) {
            (this.props as any).push(projectChangeRedirectUrl);
          } else {
            (this.props as any).push(
              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              buildProjectChangeRedirectUrl(curProjectId),
            );
          }
        }
      }

      render() {
        const {
          remountOnProjectChangeActiveProject,
          remountOnProjectChangeActiveCohort,
          ...rest
        } = this.props as any;

        const toPass = {
          ...rest,
          ...(includeActiveProject && {
            activeProject: remountOnProjectChangeActiveProject,
          }),
        };
        if (onlyMountWithProject && !remountOnProjectChangeActiveProject) {
          return null;
        } else if (remountOnChange) {
          const projectIdPortion =
            remountOnProjectChangeActiveProject?.id || NULL_PROJECT_CHILD_KEY;
          const cohortIdPortion = remountOnCohortChange
            ? remountOnProjectChangeActiveCohort || NULL_COHORT_CHILD_KEY
            : NULL_COHORT_CHILD_KEY;
          const key = `${projectIdPortion}-${cohortIdPortion}`;
          // changing the wrapped component's key triggers a remount
          return <WrappedComponent key={key} {...toPass} />;
        } else {
          return <WrappedComponent {...toPass} />;
        }
      }
    }

    const mapStateToProps = (state: any) => ({
      remountOnProjectChangeActiveProject:
        projectPickerSelectors.selectActiveProject(state),
      remountOnProjectChangeActiveCohort:
        projectCohortPickerSelectors.selectActiveProjectCohortId(state), // avoid prop name collision
    });

    const mapDispatchToProps = {
      push,
    };

    return compose(connect(mapStateToProps, mapDispatchToProps))(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      WatchForProjectChange,
    );
  };

export default watchForProjectChange as any;
